/**
 * SettingsView.js
 *
 * Copyright (c) 2026 Eric Seigne <eric.seigne@cap-rel.fr>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import { Buhlmann } from '../core/Buhlmann.js'
import { CCR } from '../core/CCR.js'
import { Gas } from '../core/Gas.js'
import { describeO2Flush } from '../core/DayPlanner.js'
import { flushFigures, planEnvironment } from '../utils/planUtils.js'
import { readBoundedNumber, escapeHtml } from '../utils/viewUtils.js'
import { Store } from '../store/Store.js'
import { i18n, languages } from '../i18n/index.js'
import { APP_VERSION } from '../utils/SavedPlans.js'
import { supportEntryKey } from '../utils/support.js'
import { balancedPartition } from '../utils/columnBalance.js'

export class SettingsView {
  constructor(container) {
    this.container = container
    // Pending timer restoring the save button label, cancelled by destroy()
    this.saveFeedbackTimeout = null
    // Store subscription refreshing the "recalculate the plan" notice,
    // dropped by destroy()
    this.unsubscribe = null
    // Column balancing: observer watching the cards whose height changes on
    // their own (the CCR block folding, a GF preset added), media query
    // telling desktop from mobile, and pending animation frame. All released
    // by destroy().
    this.cardObserver = null
    this.desktopQuery = null
    this.desktopQueryHandler = null
    this.layoutFrame = null
  }

  t(key, params) {
    return i18n.t(key, params)
  }

  // Translate a key and log when no dictionary carries it. i18n.t() returns the
  // key itself in that case, so without this trace a raw identifier would show
  // up in the interface with nothing anywhere to explain why.
  tChecked(key, params) {
    const text = this.t(key, params)
    if (text === key) {
      console.error('SettingsView: no translation for "%s", showing the raw key', key)
    }
    return text
  }

  destroy() {
    // The save button feedback timer outlives the view (1500 ms), so it must
    // be cancelled or it fires on a detached button after navigation
    if (this.saveFeedbackTimeout) {
      clearTimeout(this.saveFeedbackTimeout)
      this.saveFeedbackTimeout = null
    }
    // The store subscription outlives the view too: left in place it would
    // look for the notice of a page that is no longer displayed at every
    // single state change.
    if (this.unsubscribe) {
      this.unsubscribe()
      this.unsubscribe = null
    }
    this.releaseColumnBalancing()
    // Nothing else to unwire: no listener outside this view's container. Every
    // listener is bound to a node produced by render() (the GF preset dialog is
    // part of container.innerHTML), so they are released together with the
    // container content.
    this.container = null
  }

  // Drop everything the column balancing installed outside the container: an
  // observer or a media query listener left behind would keep measuring a
  // detached page at every resize.
  releaseColumnBalancing() {
    if (this.layoutFrame !== null) {
      cancelAnimationFrame(this.layoutFrame)
      this.layoutFrame = null
    }
    if (this.cardObserver) {
      this.cardObserver.disconnect()
      this.cardObserver = null
    }
    if (this.desktopQuery && this.desktopQueryHandler) {
      this.desktopQuery.removeEventListener('change', this.desktopQueryHandler)
    }
    this.desktopQuery = null
    this.desktopQueryHandler = null
  }

  getCurrentTheme() {
    return localStorage.getItem('mvplan_theme') || 'dark'
  }

  setTheme(theme) {
    localStorage.setItem('mvplan_theme', theme)
    document.documentElement.setAttribute('data-theme', theme)
  }

  // Water type currently stored, in one of the two forms the engine accepts:
  // a Gas.WATER_PRESETS key, or a density in g/l.
  waterType() {
    return window.store.state.environment?.waterType ?? Store.DEFAULT_WATER_TYPE
  }

  // Density of the stored water, in g/l, or null when the stored value is
  // unusable (the reason is logged by Store.waterDensityOrNull).
  waterDensity() {
    return Store.waterDensityOrNull(this.waterType())
  }

  // Options of the water selector: the named presets, then the custom density.
  // Each preset carries its density so the diver reads what the name stands
  // for instead of having to know that sea water weighs 1030 g/l.
  renderWaterOptions(selected) {
    const options = Object.keys(Gas.WATER_PRESETS).map(key => {
      const labelKey = Store.WATER_PRESET_LABEL_KEYS[key]
      if (!labelKey) {
        // A preset added to the engine without its label: show the raw key
        // rather than an empty line in the selector, and say so in the console.
        console.error('SettingsView: no label key declared for the water preset "%s"', key)
      }
      const label = labelKey ? this.tChecked(labelKey) : key
      return `<option value="${key}" ${selected === key ? 'selected' : ''}>${label} (${Gas.WATER_PRESETS[key]} g/l)</option>`
    })
    options.push(`<option value="${Store.CUSTOM_WATER}" ${selected === Store.CUSTOM_WATER ? 'selected' : ''}>${this.tChecked('settings_water_custom')}</option>`)
    return options.join('')
  }

  // What the selected water actually changes, in the units a diver reads:
  // the density it stands for and the metres of water column worth one bar
  // (9.90 m/bar in the sea, 10.19 m/bar in a lake).
  // `density` is null when the stored water type cannot be resolved.
  formatWaterFigures(density) {
    if (density === null || density === undefined) return '--'
    return `${density} g/l - ${Gas.depthPerBar(density).toFixed(2)} m/bar`
  }

  // Parameters offered to the density hint, so the translated sentence can
  // quote the accepted range without the view knowing how it is worded.
  waterHintParams() {
    return { min: Gas.SALINITY_MIN, max: Gas.SALINITY_MAX }
  }

  // Write one field of the environment.
  //
  // The invalidation rule lives in the store and nowhere else (see
  // Store.PLAN_INPUT_PATHS): this screen and the plan screen write the very
  // same environment and must drop a computed plan on exactly the same
  // criterion. The difference is not cosmetic - 40 m / 30 min on air at GF
  // 30/85 with the factory settings runs 78 min in fresh water, 83 min in the
  // sea and 85 min in the Red Sea, and fresh water is the only one of the four
  // to need six deco stops instead of seven.
  updateEnvironment(field, value) {
    window.store.update(`environment.${field}`, value)
  }

  // "The plan was dropped, recalculate it" notice of this page.
  //
  // It sits at the top of the page and not inside the environment card: almost
  // every setting of this screen reaches the calculation, so the water and the
  // altitude are no longer the only ones that can drop the plan held by the
  // plan screen. The element is always in the DOM and only its visibility and
  // its sentence change, so the notice can be refreshed without re-rendering
  // the page under the field being typed into.
  renderStaleNotice() {
    return `
        <div id="settings-stale-notice" class="alert alert-warning py-2 mb-4 hidden">
          <span id="settings-stale-notice-text" class="text-xs"></span>
        </div>`
  }

  // Bring that notice in line with the store. Called after every render and on
  // every store notification, because the plan can also be dropped from the
  // plan screen or the gases screen while this one is displayed (desktop
  // dashboard).
  refreshStaleNotice() {
    const notice = document.getElementById('settings-stale-notice')
    const text = document.getElementById('settings-stale-notice-text')
    if (!notice || !text) {
      console.error('SettingsView: #settings-stale-notice or its text is missing, cannot tell that the plan must be recalculated')
      return
    }
    const key = Store.staleNoticeKey(window.store.state.results)
    if (key) text.textContent = this.tChecked(key)
    notice.classList.toggle('hidden', !key)
  }

  // Desktop breakpoint of this page: the lg: prefix the markup is written with
  static DESKTOP_QUERY = '(min-width: 1024px)'

  // Balance the columns once, then watch what can change a height afterwards:
  // a card folding open, and the window crossing the desktop breakpoint.
  setupColumnBalancing() {
    this.releaseColumnBalancing()

    const cards = this.settingsCards()
    if (cards.length === 0) {
      console.error('SettingsView.setupColumnBalancing: no settings card found, columns left as rendered')
      return
    }

    this.layoutColumns()

    if (typeof ResizeObserver === 'function') {
      // The CCR block folding and a GF preset being added change a height with
      // no re-render. Moving a card never changes its width, hence never its
      // height, so the observer cannot feed itself a loop.
      this.cardObserver = new ResizeObserver(() => this.scheduleLayout())
      cards.forEach(card => this.cardObserver.observe(card))
    } else {
      console.error('SettingsView.setupColumnBalancing: no ResizeObserver available, columns are only balanced on render')
    }

    if (typeof window.matchMedia === 'function') {
      this.desktopQuery = window.matchMedia(SettingsView.DESKTOP_QUERY)
      this.desktopQueryHandler = () => this.scheduleLayout()
      this.desktopQuery.addEventListener('change', this.desktopQueryHandler)
    } else {
      console.error('SettingsView.setupColumnBalancing: no matchMedia available, columns cannot follow the breakpoint')
    }
  }

  // Coalesce the bursts a ResizeObserver fires while a card animates open
  scheduleLayout() {
    if (this.layoutFrame !== null) return
    this.layoutFrame = requestAnimationFrame(() => {
      this.layoutFrame = null
      this.layoutColumns()
    })
  }

  // The cards, in reading order: each column holds a contiguous run, so
  // document order across the three columns is the order they were rendered in
  settingsCards() {
    if (!this.container) return []
    return [...this.container.querySelectorAll('#settings-columns .settings-card')]
  }

  layoutColumns() {
    if (!this.container) return

    const root = this.container.querySelector('#settings-columns')
    if (!root) {
      console.error('SettingsView.layoutColumns: #settings-columns is missing, columns left as rendered')
      return
    }

    const columns = [...root.querySelectorAll('.settings-column')]
    const cards = this.settingsCards()
    if (columns.length === 0 || cards.length === 0) {
      console.error('SettingsView.layoutColumns: no column or no card to balance')
      return
    }

    // Below the breakpoint the three columns are stacked blocks: splitting the
    // cards there would scatter them down the page out of order
    const assignment = this.isDesktopLayout()
      ? balancedPartition(cards.map(card => this.cardHeight(card)), columns.length)
      : cards.map(() => 0)

    // Moving the node holding the focus would drop it, and with it the caret
    // position of a field being typed into
    const focused = document.activeElement
    const caret = this.caretOf(focused)

    cards.forEach((card, i) => {
      const column = columns[assignment[i]] || columns[0]
      if (card.parentElement !== column) column.appendChild(card)
    })

    this.restoreFocus(focused, caret)
  }

  isDesktopLayout() {
    if (typeof window.matchMedia !== 'function') {
      // Nothing here can tell which breakpoint applies: leaving every card in
      // the first column is what the markup already renders
      console.error('SettingsView.isDesktopLayout: no matchMedia available, assuming the stacked layout')
      return false
    }
    return window.matchMedia(SettingsView.DESKTOP_QUERY).matches
  }

  // Height a card takes in a column, margin included: a column of five cards
  // carries four gaps, and ignoring them skews the split
  cardHeight(card) {
    const margin = parseFloat(getComputedStyle(card).marginBottom)
    return card.offsetHeight + (Number.isFinite(margin) ? margin : 0)
  }

  caretOf(element) {
    if (!element || typeof element.selectionStart !== 'number') return null
    return { start: element.selectionStart, end: element.selectionEnd }
  }

  restoreFocus(element, caret) {
    if (!element || element === document.body) return
    if (document.activeElement === element) return
    if (!this.container?.contains(element)) return

    try {
      element.focus()
      if (caret && typeof element.setSelectionRange === 'function') {
        element.setSelectionRange(caret.start, caret.end)
      }
    } catch (err) {
      console.error('SettingsView.restoreFocus: could not give the focus back after balancing the columns', err)
    }
  }

  // Build and inject the full settings page, then attach event listeners
  render() {
    const { settings, ccr } = window.store.state
    const currentLocale = i18n.getLocale()

    this.container.innerHTML = `
      <div class="pb-20 lg:pb-4">
        ${this.renderStaleNotice()}
        <!-- Every card is rendered in the first column and moved to its own by
             layoutColumns() once its height is known. Three piles fixed at
             write time left the first column half empty and the third one a
             screen longer, and CSS columns fill against a target height of
             their own and did no better. -->
        <div id="settings-columns" class="lg:grid lg:grid-cols-3 lg:gap-4 lg:items-start">
          <div class="settings-column">
            ${this.renderGeneralSettings(settings, currentLocale)}
            ${this.renderDiveSettings(settings)}
            ${this.renderEnvironmentSettings(settings, ccr)}
          </div>
          <div class="settings-column"></div>
          <div class="settings-column"></div>
        </div>
        ${this.renderPageFooter()}
      </div>
      ${this.renderGFPresetModal()}
    `

    this.bindEvents()
    // Listeners are bound to the cards themselves, so balancing after
    // bindEvents() moves them with their handlers attached
    this.setupColumnBalancing()
    // A plan may already have been dropped before this page was opened.
    this.refreshStaleNotice()

    // Follow the store so the notice stays true when the plan is dropped from
    // another screen mounted at the same time (desktop dashboard). Only the
    // notice is refreshed: re-rendering the page would destroy the field the
    // diver is typing into, since almost every input here writes on change.
    if (this.unsubscribe) {
      this.unsubscribe()
      this.unsubscribe = null
    }
    this.unsubscribe = window.store.subscribe(() => this.refreshStaleNotice())
  }

  // Language, theme, diver names, algorithm, gradient factors and presets
  renderGeneralSettings(settings, currentLocale) {
    return `
        <!-- Language & Theme -->
        <div class="settings-card card bg-base-100 shadow-sm mb-4">
          <div class="card-body p-4 space-y-4">
            <div>
              <h2 class="card-title text-sm">${this.t('settings_language')}</h2>
              <select id="language" class="select select-bordered select-sm w-full mt-2">
                ${languages.map(lang => `
                  <option value="${lang.code}" ${currentLocale === lang.code ? 'selected' : ''}>${lang.flag} ${lang.name}</option>
                `).join('')}
              </select>
            </div>
            <div>
              <h2 class="card-title text-sm">${this.t('settings_theme')}</h2>
              <select id="theme" class="select select-bordered select-sm w-full mt-2">
                <option value="dark" ${this.getCurrentTheme() === 'dark' ? 'selected' : ''}>${this.t('settings_theme_dark')}</option>
                <option value="light" ${this.getCurrentTheme() === 'light' ? 'selected' : ''}>${this.t('settings_theme_light')}</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Default Diver Names -->
        <div class="settings-card card bg-base-100 shadow-sm mb-4">
          <div class="card-body p-4">
            <h2 class="card-title text-sm">${this.t('settings_divers')}</h2>
            <input type="text" id="diver-names" value="${settings.diverNames || ''}"
              placeholder="Jean, Marie, Pierre..."
              class="input input-bordered input-sm w-full" />
            <label class="label py-0">
              <span class="label-text-alt text-xs opacity-70">${this.t('settings_divers_hint')}</span>
            </label>
          </div>
        </div>

        <!-- Algorithm -->
        <div class="settings-card card bg-base-100 shadow-sm mb-4">
          <div class="card-body p-4">
            <h2 class="card-title text-sm">${this.t('settings_algorithm')}</h2>
            <select id="algorithm" class="select select-bordered select-sm w-full">
              <option value="${Buhlmann.ALGORITHMS.ZHL16C}" ${settings.algorithm === Buhlmann.ALGORITHMS.ZHL16C ? 'selected' : ''}>Buhlmann ZHL-16C</option>
              <option value="${Buhlmann.ALGORITHMS.ZHL16B}" ${settings.algorithm === Buhlmann.ALGORITHMS.ZHL16B ? 'selected' : ''}>Buhlmann ZHL-16B</option>
            </select>
          </div>
        </div>

        <!-- Gradient Factors -->
        <div class="settings-card card bg-base-100 shadow-sm mb-4">
          <div class="card-body p-4">
            <h2 class="card-title text-sm">${this.t('settings_gf')}</h2>

            <div class="grid grid-cols-2 gap-4">
              <div class="form-control">
                <label class="label py-1">
                  <span class="label-text text-xs">${this.t('settings_gf_low')}</span>
                  <span class="label-text-alt" id="gf-low-val">${Math.round(settings.gfLow * 100)}%</span>
                </label>
                <input type="range" id="gf-low" min="10" max="100" value="${settings.gfLow * 100}"
                  class="range range-primary range-sm" />
              </div>

              <div class="form-control">
                <label class="label py-1">
                  <span class="label-text text-xs">${this.t('settings_gf_high')}</span>
                  <span class="label-text-alt" id="gf-high-val">${Math.round(settings.gfHigh * 100)}%</span>
                </label>
                <input type="range" id="gf-high" min="10" max="100" value="${settings.gfHigh * 100}"
                  class="range range-primary range-sm" />
              </div>
            </div>

            <!-- GF Presets -->
            <div class="divider my-2"></div>
            <div class="flex items-center justify-between">
              <div>
                <span class="text-sm font-medium">${this.t('settings_gf_presets')}</span>
                <p class="text-xs opacity-70">${this.t('settings_gf_presets_hint')}</p>
              </div>
              <button id="add-gf-preset-btn" class="btn btn-xs btn-primary">+</button>
            </div>
            <div id="gf-presets-list" class="flex gap-2 mt-2 flex-wrap">
              ${(settings.gfPresets || []).map((preset, i) => `
                <div class="badge badge-lg gap-1 cursor-pointer gf-preset-item" data-gf="${preset.gfLow}/${preset.gfHigh}">
                  ${escapeHtml(preset.name)}
                  <button class="btn btn-ghost btn-xs px-0 delete-gf-preset-btn" data-index="${i}">x</button>
                </div>
              `).join('')}
            </div>
          </div>
        </div>
    `
  }

  // Descent/ascent rates, last stop, gas switch, RMV, ppO2, units
  renderDiveSettings(settings) {
    return `
        <!-- Rates & Depths -->
        <div class="settings-card card bg-base-100 shadow-sm mb-4">
          <div class="card-body p-4">
            <h2 class="card-title text-sm">${this.t('settings_rates')}</h2>

            <div class="grid grid-cols-3 gap-3">
              <div class="form-control">
                <label class="label py-1">
                  <span class="label-text text-xs">${this.t('settings_descent')} (m/min)</span>
                </label>
                <input type="number" id="descent-rate" value="${settings.descentRate}" min="5" max="30"
                  class="input input-bordered input-sm w-full" />
              </div>

              <div class="form-control">
                <label class="label py-1">
                  <span class="label-text text-xs">${this.t('settings_ascent')} (m/min)</span>
                </label>
                <input type="number" id="ascent-rate" value="${settings.ascentRate}" min="3" max="18"
                  class="input input-bordered input-sm w-full" />
              </div>

              <div class="form-control">
                <label class="label py-1">
                  <span class="label-text text-xs">${this.t('settings_deco_ascent')} (m/min)</span>
                </label>
                <input type="number" id="deco-ascent-rate" value="${settings.decoAscentRate}" min="1" max="9"
                  class="input input-bordered input-sm w-full" />
              </div>

              <div class="form-control">
                <label class="label py-1">
                  <span class="label-text text-xs">${this.t('settings_last_stop_ascent')} (m/min)</span>
                </label>
                <input type="number" id="last-stop-ascent-rate" value="${settings.lastStopAscentRate}" min="1" max="9"
                  class="input input-bordered input-sm w-full" />
              </div>

              <div class="form-control">
                <label class="label py-1">
                  <span class="label-text text-xs">${this.t('settings_last_stop')} (m)</span>
                </label>
                <select id="last-stop" class="select select-bordered select-sm w-full">
                  <option value="3" ${settings.lastStopDepth === 3 ? 'selected' : ''}>3m</option>
                  <option value="6" ${settings.lastStopDepth === 6 ? 'selected' : ''}>6m</option>
                </select>
              </div>
              <div class="form-control">
                <label class="label py-1">
                  <span class="label-text text-xs">${this.t('settings_safety_stop')}</span>
                </label>
                <input type="number" id="safety-stop" value="${settings.safetyStopTime ?? 3}" min="0" max="10" step="1"
                  class="input input-bordered input-sm w-full" />
              </div>
            </div>

          </div>
        </div>

        <!-- Segment time and gas switches. Split off from the rates card:
             four blocks separated by dividers made a single card a thousand
             pixels tall, and a card that tall pins the column split wherever
             it lands, leaving the first column half empty. -->
        <div class="settings-card card bg-base-100 shadow-sm mb-4">
          <div class="card-body p-4">
            <h2 class="card-title text-sm">${this.t('settings_time_and_gas')}</h2>

            <div class="flex items-center justify-between">
              <div>
                <span class="text-sm">${this.t('settings_descent_included')}</span>
                <p class="text-xs opacity-70">${this.t('settings_descent_included_hint')}</p>
              </div>
              <input type="checkbox" id="descent-included" class="toggle toggle-primary toggle-sm"
                ${settings.descentTimeIncluded ? 'checked' : ''} />
            </div>

            <div class="divider my-2"></div>

            <div class="form-control">
              <label class="label py-1">
                <span class="label-text text-xs">${this.t('settings_gas_switch_duration')} (min)</span>
              </label>
              <input type="number" id="gas-switch-duration" value="${settings.gasSwitchDuration ?? 2}" min="0" max="5" step="1"
                class="input input-bordered input-sm w-full" />
            </div>

            <div class="flex items-center justify-between">
              <div>
                <span class="text-sm">${this.t('settings_gas_switch_at_stop')}</span>
                <p class="text-xs opacity-70">${this.t('settings_gas_switch_at_stop_hint')}</p>
              </div>
              <input type="checkbox" id="gas-switch-at-stop" class="toggle toggle-primary toggle-sm"
                ${settings.gasSwitchAtStop ? 'checked' : ''} />
            </div>
          </div>
        </div>

        <!-- Loop flushes -->
        <div class="settings-card card bg-base-100 shadow-sm mb-4">
          <div class="card-body p-4">
            <div class="flex items-center justify-between">
              <h2 class="card-title text-sm">${this.t('settings_ccr_flush')}</h2>
              <input type="checkbox" id="ccr-flush-enabled" class="toggle toggle-primary toggle-sm"
                ${settings.ccrFlushEnabled === true ? 'checked' : ''} />
            </div>
            <p class="text-xs opacity-70">${this.t('settings_ccr_flush_hint')}</p>

            <div class="form-control ${settings.ccrFlushEnabled === true ? '' : 'hidden'}" id="ccr-flush-duration-row">
              <label class="label py-1">
                <span class="label-text text-xs">${this.t('settings_ccr_flush_duration')}</span>
              </label>
              <input type="number" id="ccr-flush-duration" value="${settings.ccrFlushDuration ?? 1}" min="1" max="10" step="1"
                class="input input-bordered input-sm w-full" />
            </div>
          </div>
        </div>

        <!-- RMV Rates -->
        <div class="settings-card card bg-base-100 shadow-sm mb-4">
          <div class="card-body p-4">
            <h2 class="card-title text-sm">${this.t('settings_rmv')} (L/min)</h2>

            <div class="grid grid-cols-2 gap-4">
              <div class="form-control">
                <label class="label py-1">
                  <span class="label-text text-xs">${this.t('settings_rmv_bottom')}</span>
                </label>
                <input type="number" id="rmv-bottom" value="${settings.rmvBottom}" min="5" max="50"
                  class="input input-bordered input-sm w-full" />
              </div>

              <div class="form-control">
                <label class="label py-1">
                  <span class="label-text text-xs">${this.t('settings_rmv_deco')}</span>
                </label>
                <input type="number" id="rmv-deco" value="${settings.rmvDeco}" min="5" max="50"
                  class="input input-bordered input-sm w-full" />
              </div>
            </div>
          </div>
        </div>

        <!-- ppO2 Limits -->
        <div class="settings-card card bg-base-100 shadow-sm mb-4">
          <div class="card-body p-4">
            <h2 class="card-title text-sm">${this.t('settings_ppo2')}</h2>

            <div class="grid grid-cols-2 gap-4">
              <div class="form-control">
                <label class="label py-1">
                  <span class="label-text text-xs">${this.t('settings_ppo2_working')} (bar)</span>
                </label>
                <input type="number" id="ppo2-max" value="${settings.ppO2Max}" min="1.0" max="1.6" step="0.1"
                  class="input input-bordered input-sm w-full" />
              </div>

              <div class="form-control">
                <label class="label py-1">
                  <span class="label-text text-xs">${this.t('settings_ppo2_deco')} (bar)</span>
                </label>
                <input type="number" id="ppo2-deco" value="${settings.ppO2Deco}" min="1.2" max="1.6" step="0.1"
                  class="input input-bordered input-sm w-full" />
              </div>
            </div>
          </div>
        </div>

        <!-- Units -->
        <div class="settings-card card bg-base-100 shadow-sm mb-4">
          <div class="card-body p-4">
            <h2 class="card-title text-sm">${this.t('settings_units')}</h2>
            <select id="units" class="select select-bordered select-sm w-full">
              <option value="metric" ${settings.units === 'metric' ? 'selected' : ''}>${this.t('settings_units_metric')}</option>
              <option value="imperial" ${settings.units === 'imperial' ? 'selected' : ''}>${this.t('settings_units_imperial')}</option>
            </select>
          </div>
        </div>
    `
  }

  // Environment, CCR, safety alerts and school mode
  renderEnvironmentSettings(settings, ccr) {
    return `
        <!-- Environment -->
        <div class="settings-card card bg-base-100 shadow-sm mb-4">
          <div class="card-body p-4">
            <h2 class="card-title text-sm">${this.t('settings_environment')}</h2>

            <div class="grid grid-cols-3 gap-4 mt-2">
              <div class="form-control">
                <label class="label py-1">
                  <span class="label-text text-xs">${this.t('settings_gas_temp')}</span>
                </label>
                <div class="join">
                  <input type="number" id="gas-temp" value="${window.store.state.environment?.gasTemp ?? 35}" min="20" max="40" step="1"
                    class="input input-bordered input-sm w-full join-item" />
                  <span class="join-item btn btn-sm no-animation">°C</span>
                </div>
              </div>

              <div class="form-control">
                <label class="label py-1">
                  <span class="label-text text-xs">${this.t('settings_water_type')}</span>
                </label>
                <select id="water-type" class="select select-bordered select-sm w-full">
                  ${this.renderWaterOptions(Store.waterPresetKey(this.waterType()))}
                </select>
              </div>

              <div class="form-control">
                <label class="label py-1">
                  <span class="label-text text-xs">${this.t('settings_altitude')}</span>
                </label>
                <div class="join">
                  <input type="number" id="altitude" value="${window.store.state.environment?.altitude ?? 0}" min="0" max="4000" step="100"
                    class="input input-bordered input-sm w-full join-item" />
                  <span class="join-item btn btn-sm no-animation">m</span>
                </div>
              </div>
            </div>

            <!-- Custom water density, shown only when the selector is on "custom" -->
            <div id="water-density-row" class="form-control mt-2 ${Store.waterPresetKey(this.waterType()) === Store.CUSTOM_WATER ? '' : 'hidden'}">
              <label class="label py-1">
                <span class="label-text text-xs">${this.tChecked('settings_water_density')}</span>
              </label>
              <div class="join">
                <input type="number" id="water-density" value="${this.waterDensity() ?? ''}"
                  min="${Gas.SALINITY_MIN}" max="${Gas.SALINITY_MAX}" step="10"
                  class="input input-bordered input-sm w-full join-item" />
                <span class="join-item btn btn-sm no-animation">g/l</span>
              </div>
              <label class="label py-0">
                <span class="label-text-alt text-xs opacity-70">${this.tChecked('settings_water_density_hint', this.waterHintParams())}</span>
              </label>
              <p id="water-density-error" class="text-xs text-error hidden"></p>
            </div>

            <label class="label py-0">
              <span id="water-figures" class="label-text-alt text-xs opacity-70">${this.formatWaterFigures(this.waterDensity())}</span>
            </label>

            <label class="label py-0">
              <span class="label-text-alt text-xs opacity-70">${this.t('settings_environment_hint')}</span>
            </label>
          </div>
        </div>

        <!-- CCR Settings -->
        <div class="settings-card card bg-base-100 shadow-sm mb-4">
          <div class="card-body p-4">
            <div class="flex items-center justify-between">
              <h2 class="card-title text-sm">${this.t('settings_ccr')}</h2>
              <input type="checkbox" id="ccr-enabled" class="toggle toggle-primary toggle-sm"
                ${ccr.enabled ? 'checked' : ''} />
            </div>

            <div id="ccr-settings" class="${ccr.enabled ? '' : 'hidden'} space-y-3 mt-3">
              <!-- Setpoint Mode -->
              <div class="form-control">
                <label class="label py-1">
                  <span class="label-text text-xs">${this.t('settings_setpoint_mode')}</span>
                </label>
                <select id="ccr-mode" class="select select-bordered select-sm w-full">
                  <option value="${CCR.MODES.FIXED}" ${ccr.mode === CCR.MODES.FIXED ? 'selected' : ''}>${this.t('settings_setpoint_fixed')}</option>
                  <option value="${CCR.MODES.DYNAMIC}" ${ccr.mode === CCR.MODES.DYNAMIC ? 'selected' : ''}>${this.t('settings_setpoint_dynamic')}</option>
                </select>
              </div>

              <!-- Fixed Setpoint -->
              <div id="fixed-sp-settings" class="${ccr.mode === CCR.MODES.FIXED ? '' : 'hidden'}">
                <div class="form-control">
                  <label class="label py-1">
                    <span class="label-text text-xs">Setpoint (bar)</span>
                  </label>
                  <input type="number" id="fixed-setpoint" value="${ccr.fixedSetpoint}" min="0.7" max="1.6" step="0.1"
                    class="input input-bordered input-sm w-full" />
                </div>
              </div>

              <!-- Dynamic Setpoints -->
              <div id="dynamic-sp-settings" class="${ccr.mode === CCR.MODES.DYNAMIC ? '' : 'hidden'}">
                <div class="grid grid-cols-3 gap-2">
                  <div class="form-control">
                    <label class="label py-1">
                      <span class="label-text text-xs">${this.t('settings_descent')}</span>
                    </label>
                    <input type="number" id="sp-descent" value="${ccr.dynamicSetpoints.descent}"
                      min="0.5" max="1.6" step="0.1" class="input input-bordered input-sm w-full" />
                  </div>
                  <div class="form-control">
                    <label class="label py-1">
                      <span class="label-text text-xs">${this.t('settings_rmv_bottom')}</span>
                    </label>
                    <input type="number" id="sp-bottom" value="${ccr.dynamicSetpoints.bottom}"
                      min="0.7" max="1.6" step="0.1" class="input input-bordered input-sm w-full" />
                  </div>
                  <div class="form-control">
                    <label class="label py-1">
                      <span class="label-text text-xs">${this.t('settings_ascent')}</span>
                    </label>
                    <input type="number" id="sp-ascent" value="${ccr.dynamicSetpoints.ascent}"
                      min="0.5" max="1.6" step="0.1" class="input input-bordered input-sm w-full" />
                  </div>
                </div>
              </div>

              <!-- Oxygen flush of the shallow stops. Here and not in the loop
                   flush card: it is part of how this rebreather is dived, like
                   the setpoints just above, and it is described once instead of
                   being retyped on every dive. -->
              <div class="divider my-2 text-xs opacity-50">${this.t('settings_ccr_o2_flush')}</div>
              <div class="flex items-center justify-between">
                <div>
                  <span class="text-sm">${this.t('settings_ccr_o2_flush')}</span>
                  <p class="text-xs opacity-70">${this.t('settings_ccr_o2_flush_hint')}</p>
                </div>
                <input type="checkbox" id="ccr-o2-flush-enabled" class="toggle toggle-primary toggle-sm"
                  ${settings.ccrO2FlushEnabled === true ? 'checked' : ''} />
              </div>
              <div id="ccr-o2-flush-rows" class="${settings.ccrO2FlushEnabled === true ? '' : 'hidden'}">
                <div class="grid grid-cols-2 gap-2">
                  <div class="form-control">
                    <label class="label py-1">
                      <span class="label-text text-xs">${this.t('settings_ccr_o2_flush_depth')}</span>
                    </label>
                    <input type="number" id="ccr-o2-flush-depth" value="${settings.ccrO2FlushDepth ?? 6}"
                      min="1" max="12" step="1" class="input input-bordered input-sm w-full" />
                  </div>
                  <div class="form-control">
                    <label class="label py-1">
                      <span class="label-text text-xs">${this.t('settings_ccr_o2_flush_time')}</span>
                    </label>
                    <input type="number" id="ccr-o2-flush-time" value="${settings.ccrO2FlushTime ?? 1}"
                      min="1" max="10" step="1" class="input input-bordered input-sm w-full" />
                  </div>
                </div>
                <p id="ccr-o2-flush-figures" class="text-xs mt-1">${this.o2FlushFiguresText()}</p>
              </div>

              <!-- Bailout Settings -->
              <div class="divider my-2 text-xs opacity-50">${this.t('settings_bailout')}</div>
              <div class="form-control">
                <label class="label py-1">
                  <span class="label-text text-xs">${this.t('settings_bail_delay')}</span>
                </label>
                <input type="number" id="bail-delay" value="${settings.bailDelay}" min="0" max="10" step="1"
                  class="input input-bordered input-sm w-full" />
                <label class="label py-0">
                  <span class="label-text-alt text-xs opacity-70">${this.t('settings_bail_delay_hint')}</span>
                </label>
              </div>

              <div class="grid grid-cols-2 gap-3 mt-2">
                <div class="form-control">
                  <label class="label py-1">
                    <span class="label-text text-xs">${this.t('settings_rmv_stress')} (L/min)</span>
                  </label>
                  <input type="number" id="rmv-stress" value="${settings.rmvStress}" min="10" max="60" step="1"
                    class="input input-bordered input-sm w-full" />
                </div>

                <div class="form-control">
                  <label class="label py-1">
                    <span class="label-text text-xs">${this.t('settings_stress_until')}</span>
                  </label>
                  <select id="stress-until" class="select select-bordered select-sm w-full">
                    <option value="first_stop" ${settings.stressUntil === 'first_stop' ? 'selected' : ''}>${this.t('settings_stress_first_stop')}</option>
                    <option value="gas_switch" ${settings.stressUntil === 'gas_switch' ? 'selected' : ''}>${this.t('settings_stress_gas_switch')}</option>
                    <option value="surface" ${settings.stressUntil === 'surface' ? 'selected' : ''}>${this.t('settings_stress_surface')}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Alert Options -->
        <div class="settings-card card bg-base-100 shadow-sm mb-4">
          <div class="card-body p-4">
            <h2 class="card-title text-sm">${this.t('settings_alerts')}</h2>
            <p class="text-xs opacity-70 mb-3">${this.t('settings_alerts_hint')}</p>

            <div class="space-y-2">
              <div class="flex items-center justify-between">
                <span class="text-sm">${this.t('settings_alerts_narcosis')}</span>
                <input type="checkbox" id="show-narcosis-alerts" class="toggle toggle-primary toggle-sm"
                  ${settings.showNarcosisAlerts !== false ? 'checked' : ''} />
              </div>

              <div class="flex items-center justify-between">
                <span class="text-sm">${this.t('settings_alerts_density')}</span>
                <input type="checkbox" id="show-density-alerts" class="toggle toggle-primary toggle-sm"
                  ${settings.showDensityAlerts !== false ? 'checked' : ''} />
              </div>

              <div class="flex items-center justify-between">
                <span class="text-sm">${this.t('settings_alerts_show_density')}</span>
                <input type="checkbox" id="show-gas-density" class="toggle toggle-primary toggle-sm"
                  ${settings.showGasDensity !== false ? 'checked' : ''} />
              </div>

              <div class="flex items-center justify-between">
                <span class="text-sm">${this.t('settings_alerts_icd')}</span>
                <input type="checkbox" id="show-icd-alerts" class="toggle toggle-primary toggle-sm"
                  ${settings.showIcdAlerts !== false ? 'checked' : ''} />
              </div>
            </div>
          </div>
        </div>

        <!-- PDF Options -->
        <div class="settings-card card bg-base-100 shadow-sm mb-4">
          <div class="card-body p-4">
            <h2 class="card-title text-sm">${this.t('settings_pdf')}</h2>

            <div class="flex items-center justify-between">
              <div class="flex flex-col">
                <span class="text-sm">${this.t('settings_pdf_include_profile')}</span>
                <span class="text-xs opacity-70">${this.t('settings_pdf_include_profile_hint')}</span>
              </div>
              <input type="checkbox" id="pdf-include-profile" class="toggle toggle-primary toggle-sm"
                ${settings.pdfIncludeProfile !== false ? 'checked' : ''} />
            </div>
          </div>
        </div>

        <!-- School Mode -->
        <div class="settings-card card bg-base-100 shadow-sm mb-4">
          <div class="card-body p-4">
            <div class="flex items-center justify-between">
              <h2 class="card-title text-sm">${this.t('settings_school_mode')}</h2>
              <input type="checkbox" id="school-mode-enabled" class="toggle toggle-primary toggle-sm"
                ${settings.schoolMode ? 'checked' : ''} />
            </div>
            <p class="text-xs opacity-70 mb-3">${this.t('settings_school_mode_hint')}</p>

            <div id="school-mode-presets" class="${settings.schoolMode ? '' : 'hidden'}">
              <span class="text-xs font-semibold">${this.t('settings_school_presets')}</span>
              <div class="space-y-1 mt-2">
                ${(settings.gfPresets || []).map((preset, idx) => `
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" class="checkbox checkbox-primary checkbox-sm school-preset-cb"
                      data-index="${idx}"
                      ${(settings.schoolModePresets || []).includes(idx) ? 'checked' : ''}
                    />
                    <span class="text-sm">${escapeHtml(preset.name)} (GF ${preset.gfLow}/${preset.gfHigh})</span>
                  </label>
                `).join('')}
              </div>
            </div>
          </div>
        </div>

    `
  }

  // Everything that belongs to the page rather than to one setting: the two
  // actions, the disclaimer, the support entry and the version. Kept out of
  // the masonry, which fills column by column and would otherwise drop the
  // save button halfway down a column.
  renderPageFooter() {
    return `
        <!-- Actions -->
        <div class="card bg-base-100 shadow-sm mb-4">
          <div class="card-body p-4">
            <div class="flex gap-2 lg:justify-end">
              <button id="reset-btn" class="btn btn-error btn-sm flex-1 lg:flex-none lg:w-48">${this.t('reset')}</button>
              <button id="save-settings-btn" class="btn btn-success btn-sm flex-1 lg:flex-none lg:w-48">${this.t('save')}</button>
            </div>
          </div>
        </div>

        <div class="lg:grid lg:grid-cols-3 lg:gap-4">
          <!-- Disclaimer -->
          <div class="card bg-base-100 shadow-sm mb-4 lg:col-span-2">
            <div class="card-body p-4">
              <h2 class="card-title text-sm">${this.t('disclaimer_title')}</h2>
              <p class="text-xs opacity-70">${this.t('disclaimer_text')}</p>
            </div>
          </div>

          <!-- Support the project -->
          <div class="card bg-base-100 shadow-sm mb-4">
            <div class="card-body p-4 justify-center">
              <button id="support-btn" class="btn btn-outline btn-primary btn-sm btn-block gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                ${this.t(supportEntryKey())}
              </button>
            </div>
          </div>
        </div>

        <!-- Version -->
        <div class="text-center text-xs opacity-50 py-4">
          mvplan v${APP_VERSION}
        </div>
    `
  }

  // GF preset creation modal dialog
  renderGFPresetModal() {
    return `
      <dialog id="gf-preset-modal" class="modal">
        <div class="modal-box">
          <h3 class="font-bold text-lg">${this.t('settings_gf_add_preset')}</h3>
          <div class="grid grid-cols-3 gap-3 mt-4">
            <div class="form-control">
              <label class="label"><span class="label-text">${this.t('settings_gf_preset_name')}</span></label>
              <input type="text" id="preset-name" class="input input-bordered input-sm" placeholder="30/70" />
            </div>
            <div class="form-control">
              <label class="label"><span class="label-text">${this.t('settings_gf_low')}</span></label>
              <input type="number" id="preset-gf-low" class="input input-bordered input-sm" min="10" max="100" value="30" />
            </div>
            <div class="form-control">
              <label class="label"><span class="label-text">${this.t('settings_gf_high')}</span></label>
              <input type="number" id="preset-gf-high" class="input input-bordered input-sm" min="10" max="100" value="70" />
            </div>
          </div>
          <div class="modal-action">
            <button id="cancel-gf-preset-btn" class="btn btn-ghost">${this.t('cancel')}</button>
            <button id="save-gf-preset-btn" class="btn btn-primary">${this.t('save')}</button>
          </div>
        </div>
        <form method="dialog" class="modal-backdrop"><button>close</button></form>
      </dialog>`
  }

  // Attach all event listeners by delegating to specialized bind methods
  bindEvents() {
    this.bindAlgorithmAndGF()
    this.bindDiveRates()
    this.bindGasConsumption()
    this.bindCCR()
    this.bindEnvironmentAndPpO2()
    this.bindDisplayPreferences()
    this.bindAlertsAndSchoolMode()
    this.bindActions()
  }

  // Algorithm selector and GF sliders/presets
  bindAlgorithmAndGF() {
    document.getElementById('algorithm')?.addEventListener('change', (e) => {
      window.store.update('settings.algorithm', e.target.value)
    })

    const gfLow = document.getElementById('gf-low')
    const gfHigh = document.getElementById('gf-high')

    gfLow?.addEventListener('input', (e) => {
      document.getElementById('gf-low-val').textContent = `${e.target.value}%`
      window.store.update('settings.gfLow', parseInt(e.target.value) / 100)
    })

    gfHigh?.addEventListener('input', (e) => {
      document.getElementById('gf-high-val').textContent = `${e.target.value}%`
      window.store.update('settings.gfHigh', parseInt(e.target.value) / 100)
    })

    this.container.querySelectorAll('.gf-preset-item').forEach(item => {
      item.addEventListener('click', (e) => {
        if (e.target.classList.contains('delete-gf-preset-btn')) return
        const [low, high] = item.dataset.gf.split('/')
        gfLow.value = low
        gfHigh.value = high
        gfLow.dispatchEvent(new Event('input'))
        gfHigh.dispatchEvent(new Event('input'))
      })
    })

    this.container.querySelectorAll('.delete-gf-preset-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation()
        const index = parseInt(btn.dataset.index)
        const presets = [...(window.store.state.settings.gfPresets || [])]
        presets.splice(index, 1)
        window.store.update('settings.gfPresets', presets)
        this.render()
      })
    })

    document.getElementById('add-gf-preset-btn')?.addEventListener('click', () => {
      document.getElementById('preset-name').value = ''
      document.getElementById('preset-gf-low').value = Math.round(window.store.state.settings.gfLow * 100)
      document.getElementById('preset-gf-high').value = Math.round(window.store.state.settings.gfHigh * 100)
      document.getElementById('gf-preset-modal')?.showModal()
    })

    document.getElementById('cancel-gf-preset-btn')?.addEventListener('click', () => {
      document.getElementById('gf-preset-modal')?.close()
    })

    document.getElementById('save-gf-preset-btn')?.addEventListener('click', () => {
      const gfLowVal = readBoundedNumber(document.getElementById('preset-gf-low'), { integer: true, context: 'SettingsView preset' })
      const gfHighVal = readBoundedNumber(document.getElementById('preset-gf-high'), { integer: true, context: 'SettingsView preset' })
      if (gfLowVal === null || gfHighVal === null) return
      if (gfLowVal > gfHighVal) {
        console.error('SettingsView preset: GF low %o above GF high %o, preset not saved', gfLowVal, gfHighVal)
        return
      }
      let name = document.getElementById('preset-name').value.trim()
      if (!name) name = `${gfLowVal}/${gfHighVal}`

      const presets = [...(window.store.state.settings.gfPresets || [])]
      presets.push({ name, gfLow: gfLowVal, gfHigh: gfHighVal })
      window.store.update('settings.gfPresets', presets)
      document.getElementById('gf-preset-modal')?.close()
      this.render()
    })
  }

  // Ascent/descent rates, last stop depth, descent time, gas switch, bail delay
  bindDiveRates() {
    document.getElementById('diver-names')?.addEventListener('change', (e) => {
      window.store.update('settings.diverNames', e.target.value.trim())
    })

    document.getElementById('descent-rate')?.addEventListener('change', (e) => {
      const value = readBoundedNumber(e.target, { fallback: window.store.get('settings.descentRate'), context: 'SettingsView' })
      if (value === null) return
      window.store.update('settings.descentRate', value)
    })

    document.getElementById('ascent-rate')?.addEventListener('change', (e) => {
      const value = readBoundedNumber(e.target, { fallback: window.store.get('settings.ascentRate'), context: 'SettingsView' })
      if (value === null) return
      window.store.update('settings.ascentRate', value)
    })

    document.getElementById('deco-ascent-rate')?.addEventListener('change', (e) => {
      const value = readBoundedNumber(e.target, { fallback: window.store.get('settings.decoAscentRate'), context: 'SettingsView' })
      if (value === null) return
      window.store.update('settings.decoAscentRate', value)
    })

    document.getElementById('last-stop-ascent-rate')?.addEventListener('change', (e) => {
      const value = readBoundedNumber(e.target, { fallback: window.store.get('settings.lastStopAscentRate'), context: 'SettingsView' })
      if (value === null) return
      window.store.update('settings.lastStopAscentRate', value)
    })

    document.getElementById('last-stop')?.addEventListener('change', (e) => {
      const value = readBoundedNumber(e.target, { fallback: window.store.get('settings.lastStopDepth'), integer: true, context: 'SettingsView' })
      if (value === null) return
      window.store.update('settings.lastStopDepth', value)
    })

    document.getElementById('safety-stop')?.addEventListener('change', (e) => {
      const value = readBoundedNumber(e.target, { fallback: window.store.get('settings.safetyStopTime'), integer: true, context: 'SettingsView' })
      if (value === null) return
      window.store.update('settings.safetyStopTime', value)
    })

    document.getElementById('descent-included')?.addEventListener('change', (e) => {
      window.store.update('settings.descentTimeIncluded', e.target.checked)
    })

    document.getElementById('ccr-flush-enabled')?.addEventListener('change', (e) => {
      window.store.update('settings.ccrFlushEnabled', e.target.checked)
      // The duration only means something once the feature is on, and the row
      // is shown without re-rendering the whole screen so the diver does not
      // lose the field he is in.
      document.getElementById('ccr-flush-duration-row')?.classList.toggle('hidden', !e.target.checked)
    })

    document.getElementById('ccr-flush-duration')?.addEventListener('change', (e) => {
      const value = readBoundedNumber(e.target, { fallback: window.store.get('settings.ccrFlushDuration'), integer: true, context: 'SettingsView' })
      if (value === null) return
      window.store.update('settings.ccrFlushDuration', value)
    })

    document.getElementById('gas-switch-duration')?.addEventListener('change', (e) => {
      const value = readBoundedNumber(e.target, { fallback: window.store.get('settings.gasSwitchDuration'), integer: true, context: 'SettingsView' })
      if (value === null) return
      window.store.update('settings.gasSwitchDuration', value)
    })

    document.getElementById('gas-switch-at-stop')?.addEventListener('change', (e) => {
      window.store.update('settings.gasSwitchAtStop', e.target.checked)
    })

    document.getElementById('bail-delay')?.addEventListener('change', (e) => {
      // Same guard as the altitude field: an emptied field parses to NaN and a
      // negative value is not a delay. Both would be stored, persisted, and
      // refused by the engine on every calculation until the diver finds the
      // field again. Refuse here and put the stored value back instead.
      const delay = readBoundedNumber(e.target, { fallback: window.store.get('settings.bailDelay'), integer: true, context: 'SettingsView' })
      if (delay === null) return
      window.store.update('settings.bailDelay', delay)
    })
  }

  // RMV settings (bottom, deco, stress)
  bindGasConsumption() {
    document.getElementById('rmv-stress')?.addEventListener('change', (e) => {
      const value = readBoundedNumber(e.target, { fallback: window.store.get('settings.rmvStress'), context: 'SettingsView' })
      if (value === null) return
      window.store.update('settings.rmvStress', value)
    })

    document.getElementById('stress-until')?.addEventListener('change', (e) => {
      window.store.update('settings.stressUntil', e.target.value)
    })

    document.getElementById('rmv-bottom')?.addEventListener('change', (e) => {
      const value = readBoundedNumber(e.target, { fallback: window.store.get('settings.rmvBottom'), context: 'SettingsView' })
      if (value === null) return
      window.store.update('settings.rmvBottom', value)
    })

    document.getElementById('rmv-deco')?.addEventListener('change', (e) => {
      const value = readBoundedNumber(e.target, { fallback: window.store.get('settings.rmvDeco'), context: 'SettingsView' })
      if (value === null) return
      window.store.update('settings.rmvDeco', value)
    })
  }

  // What the oxygen finish is worth, under its own fields.
  //
  // The depth alone says nothing - the whole point of the setting is the ppO2
  // it produces, and that ppO2 moves with the water and the altitude. It is
  // computed here and never stored: storing "1.6 bar" instead of "6 m" would
  // move the flush the day the diver changes site, and a saved plan would
  // replay somewhere else than where its author wrote it.
  //
  // No dive is known on this screen, so describeO2Flush is called without
  // segments: it answers on the setting, not on the profile.
  o2FlushFiguresText() {
    const status = describeO2Flush(window.store.state.settings, [])

    if (!status.active) {
      console.error('SettingsView: the oxygen flush setting is unusable (depth=%o, time=%o)', status.depth, status.time)
      return `<span class="text-error">${this.t('settings_ccr_o2_flush_invalid')}</span>`
    }

    const figures = flushFigures(status.mix, status.depth, planEnvironment())
    if (!figures) {
      console.error('SettingsView: no figures for a %om flush on %o', status.depth, status.mix?.name)
      return ''
    }
    return `<span class="opacity-70">${this.t('plan_flush_figures', {
      gas: escapeHtml(status.mix.name),
      depth: status.depth,
      ppO2: figures.ppO2.toFixed(2),
      end: Math.round(figures.end),
      density: figures.density.toFixed(1)
    })}</span>`
  }

  // Redraw that line in place. The whole screen is not re-rendered on a
  // keystroke: the field being typed into would lose its focus.
  refreshO2FlushFigures() {
    const box = document.getElementById('ccr-o2-flush-figures')
    if (!box) return
    box.innerHTML = this.o2FlushFiguresText()
  }

  // CCR enable, mode, setpoints
  bindCCR() {
    document.getElementById('ccr-enabled')?.addEventListener('change', (e) => {
      window.store.update('ccr.enabled', e.target.checked)
      document.getElementById('ccr-settings').classList.toggle('hidden', !e.target.checked)
    })

    document.getElementById('ccr-mode')?.addEventListener('change', (e) => {
      window.store.update('ccr.mode', e.target.value)
      document.getElementById('fixed-sp-settings').classList.toggle('hidden', e.target.value !== CCR.MODES.FIXED)
      document.getElementById('dynamic-sp-settings').classList.toggle('hidden', e.target.value !== CCR.MODES.DYNAMIC)
    })

    document.getElementById('fixed-setpoint')?.addEventListener('change', (e) => {
      const value = readBoundedNumber(e.target, { fallback: window.store.get('ccr.fixedSetpoint'), context: 'SettingsView' })
      if (value === null) return
      window.store.update('ccr.fixedSetpoint', value)
    })

    document.getElementById('sp-descent')?.addEventListener('change', (e) => {
      const value = readBoundedNumber(e.target, { fallback: window.store.get('ccr.dynamicSetpoints.descent'), context: 'SettingsView' })
      if (value === null) return
      window.store.update('ccr.dynamicSetpoints.descent', value)
    })

    document.getElementById('sp-bottom')?.addEventListener('change', (e) => {
      const value = readBoundedNumber(e.target, { fallback: window.store.get('ccr.dynamicSetpoints.bottom'), context: 'SettingsView' })
      if (value === null) return
      window.store.update('ccr.dynamicSetpoints.bottom', value)
    })

    document.getElementById('sp-ascent')?.addEventListener('change', (e) => {
      const value = readBoundedNumber(e.target, { fallback: window.store.get('ccr.dynamicSetpoints.ascent'), context: 'SettingsView' })
      if (value === null) return
      window.store.update('ccr.dynamicSetpoints.ascent', value)
    })

    document.getElementById('ccr-o2-flush-enabled')?.addEventListener('change', (e) => {
      window.store.update('settings.ccrO2FlushEnabled', e.target.checked)
      document.getElementById('ccr-o2-flush-rows')?.classList.toggle('hidden', !e.target.checked)
      this.refreshO2FlushFigures()
    })

    document.getElementById('ccr-o2-flush-depth')?.addEventListener('change', (e) => {
      const value = readBoundedNumber(e.target, {
        fallback: window.store.get('settings.ccrO2FlushDepth'), integer: true, context: 'SettingsView'
      })
      if (value === null) return
      window.store.update('settings.ccrO2FlushDepth', value)
      this.refreshO2FlushFigures()
    })

    document.getElementById('ccr-o2-flush-time')?.addEventListener('change', (e) => {
      const value = readBoundedNumber(e.target, {
        fallback: window.store.get('settings.ccrO2FlushTime'), integer: true, context: 'SettingsView'
      })
      if (value === null) return
      window.store.update('settings.ccrO2FlushTime', value)
    })
  }

  // Environment (gas temp, water type, altitude) and ppO2 limits
  bindEnvironmentAndPpO2() {
    // Every environment write goes through updateEnvironment so a change that
    // makes the computed plan obsolete drops it instead of leaving the plan
    // screen showing stops that belong to another site.
    document.getElementById('gas-temp')?.addEventListener('change', (e) => {
      const gasTemp = readBoundedNumber(e.target, { fallback: window.store.state.environment?.gasTemp ?? 35, integer: true, context: 'SettingsView' })
      if (gasTemp === null) return
      this.updateEnvironment('gasTemp', gasTemp)
    })

    document.getElementById('water-type')?.addEventListener('change', (e) => {
      this.selectWaterType(e.target.value)
    })

    // Typing is followed live so the metres per bar and the refusal of an out
    // of range value are immediate, but only a settled value (change: blur or
    // Enter) is written to the store.
    const densityInput = document.getElementById('water-density')
    densityInput?.addEventListener('input', () => this.applyDensityInput(false))
    densityInput?.addEventListener('change', () => this.applyDensityInput(true))

    document.getElementById('altitude')?.addEventListener('change', (e) => {
      // Same guard as the water density right above, and as PlanView: an
      // out of range or emptied altitude is refused instead of being stored
      // and silently planned at sea level.
      const altitude = parseInt(e.target.value, 10)
      if (!Store.isUsableAltitude(altitude)) {
        console.error('SettingsView: rejected altitude %o, keeping %o m',
          e.target.value, this.environment?.().altitude ?? window.store.state.environment?.altitude ?? 0)
        e.target.value = window.store.state.environment?.altitude ?? 0
        return
      }
      this.updateEnvironment('altitude', altitude)
    })

    document.getElementById('ppo2-max')?.addEventListener('change', (e) => {
      const value = readBoundedNumber(e.target, { fallback: window.store.get('settings.ppO2Max'), context: 'SettingsView' })
      if (value === null) return
      window.store.update('settings.ppO2Max', value)
    })

    document.getElementById('ppo2-deco')?.addEventListener('change', (e) => {
      const value = readBoundedNumber(e.target, { fallback: window.store.get('settings.ppO2Deco'), context: 'SettingsView' })
      if (value === null) return
      window.store.update('settings.ppO2Deco', value)
    })
  }

  // Apply a choice made in the water selector.
  // A named preset is stored as its key, which is what every state written
  // before this screen offered a density already contains. "Custom" stores the
  // density of the water currently selected: the same number the engine was
  // already using, so switching to custom never changes the plan by itself.
  selectWaterType(choice) {
    if (choice === Store.CUSTOM_WATER) {
      let density = this.waterDensity()
      if (density === null) {
        // The stored value is unusable (already logged): start the custom field
        // from sea water rather than leave the diver with an empty field.
        density = Gas.SEAWATER_SALINITY
        console.error('SettingsView: stored water type %o unusable, starting the custom density at %o g/l',
          this.waterType(), density)
      }
      this.updateEnvironment('waterType', density)
      this.refreshWaterControls()
      return
    }

    if (!Object.prototype.hasOwnProperty.call(Gas.WATER_PRESETS, choice)) {
      console.error('SettingsView: unknown water preset "%s" selected, environment left unchanged', choice)
      return
    }

    this.updateEnvironment('waterType', choice)
    this.refreshWaterControls()
  }

  // Read the custom density field, tell the diver whether it is usable, and
  // write it to the store only when it is. `commit` is false while typing
  // (input event) and true once the value is settled (change event).
  // An out of range value is refused and displayed as such: nothing is written,
  // and the rest of the screen keeps working - the whole point of validating
  // here instead of letting Gas.waterDensity throw from inside a handler.
  applyDensityInput(commit) {
    const input = document.getElementById('water-density')
    if (!input) {
      console.error('SettingsView: #water-density is missing, the custom density cannot be read')
      return
    }

    const raw = input.value.trim()
    const density = raw === '' ? NaN : Number(raw)
    const usable = Store.isUsableDensity(density)

    this.showDensityError(!usable)
    this.updateWaterFigures(usable ? density : null)

    if (!usable) {
      // Half typed values are expected while the diver types, hence the warning
      // level; a settled value that is still refused is an error.
      const reason = 'SettingsView: water density %o refused, expected a number within [%o, %o] g/l'
      if (commit) {
        console.error(reason, input.value, Gas.SALINITY_MIN, Gas.SALINITY_MAX)
      } else {
        console.warn(reason, input.value, Gas.SALINITY_MIN, Gas.SALINITY_MAX)
      }
      return
    }

    if (!commit) return
    this.updateEnvironment('waterType', density)
  }

  // Show or hide the refusal message under the density field.
  showDensityError(show) {
    const message = document.getElementById('water-density-error')
    const input = document.getElementById('water-density')
    if (!message || !input) {
      console.error('SettingsView: #water-density-error or #water-density is missing, cannot report the density')
      return
    }
    // Bounds only, no sentence: the translated hint right above already says
    // what the field expects, and this line has to stay true in six languages.
    message.textContent = `${Gas.SALINITY_MIN} - ${Gas.SALINITY_MAX} g/l`
    message.classList.toggle('hidden', !show)
    input.classList.toggle('input-error', show)
  }

  // Refresh the "1030 g/l - 9.90 m/bar" line under the water selector.
  updateWaterFigures(density) {
    const figures = document.getElementById('water-figures')
    if (!figures) {
      console.error('SettingsView: #water-figures is missing, cannot show the water column figures')
      return
    }
    figures.textContent = this.formatWaterFigures(density)
  }

  // Bring the water controls in line with the stored environment: visibility of
  // the density row, value of the density field, figures line, error cleared.
  refreshWaterControls() {
    const isCustom = Store.waterPresetKey(this.waterType()) === Store.CUSTOM_WATER
    const row = document.getElementById('water-density-row')
    if (row) {
      row.classList.toggle('hidden', !isCustom)
    } else {
      console.error('SettingsView: #water-density-row is missing, the custom density cannot be shown')
    }

    const density = this.waterDensity()
    const input = document.getElementById('water-density')
    if (input && isCustom && density !== null) input.value = density
    this.showDensityError(false)
    this.updateWaterFigures(density)
  }

  // Units, language, theme
  bindDisplayPreferences() {
    document.getElementById('units')?.addEventListener('change', (e) => {
      window.store.update('settings.units', e.target.value)
    })

    document.getElementById('language')?.addEventListener('change', (e) => {
      const langCode = e.target.value
      localStorage.setItem('mvplan_locale', langCode)
      localStorage.setItem('mvplan_language_selected', 'true')
      window.store.update('settings.language', langCode)
      window.location.reload()
    })

    document.getElementById('theme')?.addEventListener('change', (e) => {
      this.setTheme(e.target.value)
    })
  }

  // Alert visibility toggles and school mode
  bindAlertsAndSchoolMode() {
    document.getElementById('show-narcosis-alerts')?.addEventListener('change', (e) => {
      window.store.update('settings.showNarcosisAlerts', e.target.checked)
    })

    document.getElementById('show-density-alerts')?.addEventListener('change', (e) => {
      window.store.update('settings.showDensityAlerts', e.target.checked)
    })

    document.getElementById('show-gas-density')?.addEventListener('change', (e) => {
      window.store.update('settings.showGasDensity', e.target.checked)
    })

    document.getElementById('show-icd-alerts')?.addEventListener('change', (e) => {
      window.store.update('settings.showIcdAlerts', e.target.checked)
    })

    document.getElementById('pdf-include-profile')?.addEventListener('change', (e) => {
      window.store.update('settings.pdfIncludeProfile', e.target.checked)
    })

    document.getElementById('school-mode-enabled')?.addEventListener('change', (e) => {
      window.store.update('settings.schoolMode', e.target.checked)
      const presetsDiv = document.getElementById('school-mode-presets')
      if (presetsDiv) presetsDiv.classList.toggle('hidden', !e.target.checked)
    })

    this.container.querySelectorAll('.school-preset-cb').forEach(cb => {
      cb.addEventListener('change', () => {
        const selected = []
        this.container.querySelectorAll('.school-preset-cb:checked').forEach(checked => {
          selected.push(parseInt(checked.dataset.index))
        })
        if (selected.length > 3) {
          cb.checked = false
          return
        }
        window.store.update('settings.schoolModePresets', selected)
      })
    })
  }

  // Reset and Save buttons
  bindActions() {
    document.getElementById('reset-btn')?.addEventListener('click', () => {
      if (confirm(this.t('confirm_reset'))) {
        window.store.reset()
        this.render()
      }
    })

    document.getElementById('save-settings-btn')?.addEventListener('click', (e) => {
      const btn = e.target
      const originalText = btn.textContent
      window.store.saveState()
      btn.textContent = '✓'
      btn.disabled = true
      // Keep a single cancellable timer so a second click cannot orphan the
      // previous one and destroy() can always clear the pending feedback
      if (this.saveFeedbackTimeout) clearTimeout(this.saveFeedbackTimeout)
      this.saveFeedbackTimeout = setTimeout(() => {
        this.saveFeedbackTimeout = null
        btn.textContent = originalText
        btn.disabled = false
      }, 1500)
    })

    document.getElementById('support-btn')?.addEventListener('click', () => {
      window.location.hash = '/support'
    })
  }
}
